import { useEffect, useState, useRef } from "react";
import { motion, animate } from "framer-motion";
import {
    Box,
    Container,
    Grid,
    Typography,
    Button,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Link as MLink,
    Fab,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WifiIcon from "@mui/icons-material/Wifi";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import ShieldIcon from "@mui/icons-material/Shield";
import PublicIcon from "@mui/icons-material/Public";
import PaletteIcon from "@mui/icons-material/Palette";
import GetAppIcon from "@mui/icons-material/GetApp";
import { Link as RouterLink } from "react-router-dom";

const BRAND_BLUE = "#0d396e";
const BRAND_GREEN = "#00C853";

/**
 * Animated Counter (startet erst wenn sichtbar)
 */
function AnimatedCounter({ value }) {
    const [display, setDisplay] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!ref.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated) {
                    setHasAnimated(true);
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, [hasAnimated]);

    useEffect(() => {
        if (!value || !hasAnimated) return;

        const controls = animate(0, value, {
            duration: 1.8,
            ease: "easeOut",
            onUpdate(latest) {
                setDisplay(Math.floor(latest));
            },
        });

        return () => controls.stop();
    }, [value, hasAnimated]);

    return (
        <Box ref={ref} sx={{ textAlign: "center" }}>
            <Typography
                variant="h2"
                sx={{
                    fontWeight: 800,
                    color: BRAND_GREEN,
                    letterSpacing: "-1px",
                }}
            >
                {display.toLocaleString("de-DE")}
            </Typography>
            <Typography sx={{ color: "text.secondary" }}>
                erstellte QR Codes
            </Typography>
        </Box>
    );
}

export default function MainApp() {
    const [canInstall, setCanInstall] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [qrCount, setQrCount] = useState(0);

    // Load stats
    useEffect(() => {
        async function loadStats() {
            try {
                const res = await fetch("https://backend.helloqr.de/stats");
                const data = await res.json();
                setQrCount(data.count || 0);
            } catch (e) {
                console.error("Stats error", e);
            }
        }
        loadStats();
    }, []);

    // PWA
    useEffect(() => {
        const handler = (e) => {
            if (typeof e.preventDefault === "function") e.preventDefault();
            setDeferredPrompt(e);
            setCanInstall(true);
        };
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    async function handleInstall() {
        if (!deferredPrompt || typeof deferredPrompt.prompt !== "function") return;
        try {
            await deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            if (choice && choice.outcome === "accepted") {
                setCanInstall(false);
                setDeferredPrompt(null);
            }
        } catch (err) {
            console.warn("PWA install failed:", err);
        }
    }

    const featureCards = [
        { icon: <ShieldIcon fontSize="large" sx={{ color: BRAND_BLUE }} />, title: "DSGVO-konform", desc: "Keine Speicherung, keine Cookies." },
        { icon: <PublicIcon fontSize="large" sx={{ color: BRAND_BLUE }} />, title: "Viele QR-Typen", desc: "Website, WLAN, vCard, PDF & mehr." },
        { icon: <PaletteIcon fontSize="large" sx={{ color: BRAND_BLUE }} />, title: "Design & Logo", desc: "Farben & Logo individuell anpassbar." },
    ];

    const qrTypes = [
        { icon: <PublicIcon />, title: "Website", link: "/qr-code-generator" },
        { icon: <WifiIcon />, title: "WLAN", link: "/qr-code-generator" },
        { icon: <PersonIcon />, title: "Visitenkarte", link: "/qr-code-generator" },
        { icon: <DescriptionIcon />, title: "PDF", link: "/qr-code-generator" },
        { icon: <ImageIcon />, title: "Mit Logo", link: "/qr-code-generator" },
        { icon: <PaletteIcon />, title: "Farbe", link: "/qr-code-generator" },
    ];

    return (
        <Box sx={{ bgcolor: "#fff", color: "#1f2937" }}>
            {/* HERO */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <MLink component={RouterLink} to="/" underline="none">
                            <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/images/helloqr.png`}
                                alt="HelloQR Logo"
                                sx={{
                                    width: { xs: 160, md: 200 },
                                    borderRadius: 2,
                                    display: "inline-block",
                                    mb: 2,
                                }}
                            />
                        </MLink>

                        <Typography
                            variant="h2"
                            component="h1"
                            gutterBottom
                            sx={{ fontWeight: 800, color: BRAND_BLUE, lineHeight: 1.2 }}
                        >
                            Kostenloser QR Code Generator – einfach, schnell & ohne Anmeldung
                        </Typography>

                        <Typography variant="h6" sx={{ color: "text.secondary", mb: 3 }}>
                            Erstelle in Sekunden deinen individuellen QR Code für Website, WLAN,
                            Visitenkarte, PDF & mehr. 100 % kostenlos und DSGVO-konform.
                        </Typography>

                        <Button
                            component={RouterLink}
                            to="/qr-code-generator"
                            variant="contained"
                            size="large"
                            sx={{ bgcolor: BRAND_GREEN, ":hover": { bgcolor: "#00B84A" } }}
                        >
                            Jetzt QR Code erstellen
                        </Button>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Box
                                component="img"
                                src="/images/007_DKM_04.png"
                                alt="HelloQR Generator Vorschau"
                                sx={{ width: "100%" }}
                            />
                        </motion.div>
                    </Grid>
                </Grid>
            </Container>

            {/* FEATURES */}
            <Box sx={{ bgcolor: "#f8fafc", py: 10 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={3}>
                        {featureCards.map((f, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Card elevation={1} sx={{ borderRadius: 4 }}>
                                    <CardContent sx={{ textAlign: "center", py: 6 }}>
                                        <Box sx={{ display: "inline-flex", mb: 1 }}>{f.icon}</Box>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{f.title}</Typography>
                                        <Typography color="text.secondary">{f.desc}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* COUNTER SECTION */}
            <Box sx={{ py: 10 }}>
                <Container maxWidth="sm">
                    <Box
                        sx={{
                            p: 5,
                            borderRadius: 4,
                            textAlign: "center",
                            bgcolor: "#ffffff",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 800, color: BRAND_BLUE, mb: 2 }}
                        >
                            Bereits erstellt
                        </Typography>

                        <AnimatedCounter value={qrCount} />
                    </Box>
                </Container>
            </Box>

            {/* QR TYPES */}
            <Container maxWidth="lg" sx={{ py: 10 }}>
                <Typography variant="h4" align="center" sx={{ fontWeight: 800, color: BRAND_BLUE, mb: 6 }}>
                    Welche QR Codes kannst du erstellen?
                </Typography>
                <Grid container spacing={3}>
                    {qrTypes.map((t, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <Card
                                component={RouterLink}
                                to={t.link}
                                elevation={1}
                                sx={{
                                    display: "block",
                                    textDecoration: "none",
                                    color: "inherit",
                                    borderRadius: 4,
                                    bgcolor: "#fff",
                                    boxShadow: 1,
                                    transition: "box-shadow 0.2s ease",
                                    "&:hover": {
                                        boxShadow: 6,
                                        transform: "translateY(-2px)",
                                    },
                                }}
                            >
                                <CardContent sx={{ textAlign: "center", py: 6 }}>
                                    <Box sx={{ display: "inline-flex", color: BRAND_BLUE, mb: 1 }}>
                                        {t.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        QR Code für {t.title}
                                    </Typography>
                                    <Typography sx={{ color: "text.secondary", mt: 1 }}>
                                        Erstelle jetzt deinen {t.title}-QR Code kostenlos.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* FAQ */}
            <Box sx={{ bgcolor: "#f8fafc", py: 10 }}>
                <Container maxWidth="md">
                    <Typography variant="h4" align="center" sx={{ fontWeight: 800, color: BRAND_BLUE, mb: 4 }}>
                        Häufige Fragen
                    </Typography>

                    <Accordion disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Ist HelloQR wirklich kostenlos?</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography>Ja, du kannst unbegrenzt viele QR Codes kostenlos erstellen.</Typography>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Muss ich mich registrieren?</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography>Nein, HelloQR funktioniert komplett ohne Anmeldung.</Typography>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Kann ich QR Codes mit Logo erstellen?</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography>Ja, lade dein Logo hoch und passe Farbe & Design individuell an.</Typography>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Werden meine Daten gespeichert?</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography>
                                Nein. Deine Daten werden ausschließlich zur Erstellung des QR-Codes verwendet.
                                Nach der Generierung werden sie sofort vom Server gelöscht. Es erfolgt keine Speicherung
                                in einer Datenbank und keine Weitergabe an Dritte. Der gesamte Service ist vollständig DSGVO-konform.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Container>
            </Box>

            {/* CTA */}
            <Box sx={{ bgcolor: BRAND_BLUE, color: "#fff", textAlign: "center", py: 10 }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                        Bereit, deinen ersten QR Code zu erstellen?
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.8)", mb: 3 }}>
                        Schnell, sicher & kostenlos – direkt im Browser.
                    </Typography>

                    <Button
                        component={RouterLink}
                        to="/qr-code-generator"
                        variant="contained"
                        size="large"
                        sx={{ bgcolor: BRAND_GREEN, mb: 5, ":hover": { bgcolor: "#00B84A" } }}
                    >
                        Jetzt QR Code erstellen
                    </Button>

                    <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
                        <MLink component={RouterLink} to="/impressum" underline="hover" sx={{ color: "#fff" }}>
                            Impressum
                        </MLink>
                        <MLink component={RouterLink} to="/datenschutz" underline="hover" sx={{ color: "#fff" }}>
                            Datenschutz
                        </MLink>
                    </Box>
                </Container>
            </Box>

            {/* PWA FAB */}
            {canInstall && (
                <Fab
                    color="primary"
                    onClick={handleInstall}
                    sx={{ position: "fixed", bottom: 24, right: 24, bgcolor: BRAND_GREEN }}
                >
                    <GetAppIcon />
                </Fab>
            )}
        </Box>
    );
}